// WoodWop MPR exporter (Homag native format).
//
// Emits one .mpr per panel grouped by cabinet, bundled in a project ZIP.
// Each MPR carries:
//   [001]  free-text header (optional, behind the `comment` flag)
//   [H]    panel envelope: length / width / thickness / origin / mirrors
//   [100]  point drillings (shelf pins, hinge cups, screws, …)
//   [102]  contour pockets — routed slots from `DrillEntry { kind: "groove" }`
//          (back-panel rebates on side panels)
//
// Format reference: WeeKe / WoodWop manual, "MPR-Inhalte" section.
// MPR is line-oriented: section header `[NNN]`, then `key=value` lines
// or quoted lines. Numbers are stored in mm with a `.` decimal.
//
// Phase-4.2 work parked for now: [200] tool-list references.

import type { CabinetPart } from "@/lib/types";
import type { DrillEntry } from "@/lib/cabinets";
import { effectiveDrillings } from "@/lib/cabinets/bom";
import { isDoorPart } from "@/lib/cabinets/roles";
import type { ExportArtifact, Postprocessor } from "./types";
import { makeZip } from "./zip";

const INCH_TO_MM = 25.4;

const woodwopMpr: Postprocessor = {
  id: "woodwop_mpr",
  label: "WoodWop MPR (Homag CNC)",
  description: "One .mpr per panel, grouped by cabinet, ZIP'd for the project. Loads in WoodWop 7/8 simulator and on shop-floor Homag controllers.",
  category: "machine",
  extension: "mpr",
  options: [
    {
      key: "include_doors",
      label: "Include doors / faces",
      type: "boolean",
      default: false,
      help: "Doors are usually routed on a different setup; default off.",
    },
    {
      key: "origin",
      label: "Machine origin",
      type: "select",
      default: "front_left",
      choices: [
        { value: "front_left",  label: "Front-left (machine X+ = length)" },
        { value: "front_right", label: "Front-right (mirrored)" },
      ],
    },
    {
      key: "comment",
      label: "Add project header comment",
      type: "boolean",
      default: true,
    },
  ],
  generate(ctx, opts): ExportArtifact[] {
    const files: { name: string; content: string }[] = [];

    for (const { cabinet, parts } of ctx.parts) {
      const filtered = opts.include_doors
        ? parts
        : parts.filter((p) => !isDoorPart(p));

      let panelIndex = 0;
      for (const p of filtered) {
        // effectiveDrillings = class default pattern - suppressed indices
        // + user-added extras (from cabinet.partOverrides[p.id]).
        const drillings = effectiveDrillings(cabinet, p.id);
        for (let q = 0; q < (p.qty ?? 1); q++) {
          const path = `cabinet_${cabinet._id}_${cabinet.classId}/${pad(panelIndex)}_${slug(p.id)}.mpr`;
          files.push({
            name: path,
            content: renderMpr(p, cabinet._id, cabinet.classId, drillings, opts),
          });
          panelIndex++;
        }
      }
    }

    if (files.length === 0) {
      return [{
        filename: `${slug(ctx.project.name)}_mpr_empty.txt`,
        content: "No panels to export.",
        mimeType: "text/plain",
      }];
    }

    // Bundle into project zip.
    const zip = makeZip(files.map((f) => ({ name: f.name, content: f.content })));
    return [{
      filename: `${slug(ctx.project.name)}_woodwop.zip`,
      content: zip,
      mimeType: "application/zip",
    }];
  },
};

// ── MPR emitter ──────────────────────────────────────────────────

function renderMpr(
  p: CabinetPart,
  cabId: number,
  cabClass: string,
  drillings: DrillEntry[],
  opts: Record<string, string | number | boolean>,
): string {
  const Lmm = (p.w * INCH_TO_MM).toFixed(2);
  const Wmm = (p.h * INCH_TO_MM).toFixed(2);
  const Tmm = (p.thickness * INCH_TO_MM).toFixed(2);
  const originCode = opts.origin === "front_right" ? "01" : "00";
  const mirrorX    = opts.origin === "front_right" ? 1 : 0;

  const lines: string[] = [];

  if (opts.comment) {
    lines.push("[001]");
    lines.push(`"Cabinet #${cabId} ${cabClass}"`);
    lines.push(`"Part: ${p.name} (${p.id})"`);
    lines.push(`"Grain: ${p.grain}"`);
    if (drillings.length > 0)
      lines.push(`"Drillings: ${drillings.length}"`);
    lines.push("");
  }

  // [H] header — panel envelope.
  lines.push("[H]");
  lines.push(`0.0=`);                  // version/template (empty = default)
  lines.push(`1.0=${Lmm}`);             // X dim (length, mm)
  lines.push(`2.0=${Wmm}`);             // Y dim (width, mm)
  lines.push(`3.0=${Tmm}`);             // thickness (mm)
  lines.push(`4.0=${originCode}`);      // machine origin
  lines.push(`5.0=00`);                 // program comment
  lines.push(`6.0=`);                   // material descriptor
  lines.push(`7.0=`);                   // free text 1
  lines.push(`8.0=`);                   // free text 2
  lines.push(`9.0=${mirrorX}`);         // mirror X (front_right flips drilling along length)
  lines.push(`10.0=0`);                 // mirror Y
  lines.push(`11.0=4`);                 // edge profile
  lines.push(`12.0=0`);                 // edge thickness
  lines.push(`13.0=0`);                 // grain rotation flag
  lines.push(`14.0=`);                  // reserved
  lines.push("");

  // [100] point drillings / [102] contour pockets — one section per entry.
  for (const d of drillings) {
    const xmm = (d.x * INCH_TO_MM).toFixed(2);
    const ymm = (d.y * INCH_TO_MM).toFixed(2);
    const dmm = (d.diameter * INCH_TO_MM).toFixed(2);
    const depmm = (d.depth * INCH_TO_MM).toFixed(2);
    const faceFlag = d.face === "back" ? 1 : 0;

    if (d.kind === "groove" && d.endX != null) {
      // Routed slot: emit [102] contour pocket from (x,y) → (endX,endY).
      // endY falls back to y so a purely horizontal slot can omit it.
      const exmm = (d.endX * INCH_TO_MM).toFixed(2);
      const eymm = ((d.endY ?? d.y) * INCH_TO_MM).toFixed(2);
      lines.push("[102]");
      lines.push(`1.0=${xmm}`);         // X start (mm)
      lines.push(`2.0=${ymm}`);         // Y start (mm)
      lines.push(`3.0=${exmm}`);        // X end   (mm)
      lines.push(`4.0=${eymm}`);        // Y end   (mm)
      lines.push(`5.0=${dmm}`);         // slot width (= bit diameter, mm)
      lines.push(`6.0=${depmm}`);       // depth (mm)
      lines.push(`7.0=1`);              // tool index — 1 = standard router bit
      lines.push(`8.0=${faceFlag}`);    // face flag: 0=inside/top, 1=back/opposite
      lines.push("");
      continue;
    }

    lines.push("[100]");
    lines.push(`1.0=${xmm}`);           // X position (mm)
    lines.push(`2.0=${ymm}`);           // Y position (mm)
    lines.push(`3.0=0`);                // 0 = vertical drill (perpendicular to face)
    lines.push(`4.0=${dmm}`);           // diameter (mm)
    lines.push(`5.0=${depmm}`);         // depth (mm)
    lines.push(`6.0=${faceFlag}`);      // face flag: 0=top/inside, 1=back/opposite
    lines.push(`7.0="${d.kind}"`);      // tag — postprocessor / shop chooser
    lines.push("");
  }

  return lines.join("\r\n");  // WoodWop expects CRLF
}

function pad(n: number): string {
  return n.toString().padStart(3, "0");
}

function slug(s: string): string {
  return s.replace(/[^\w]+/g, "_").replace(/^_+|_+$/g, "") || "panel";
}

export default woodwopMpr;
